/**
 * The wire shapes of the Bedrock price endpoints in `Rws.AiInsights.Host.Status.StatusEndpoints` (AIA-109 pricing page): the price
 * catalogue, and the create, update and delete of a price override. Field names and casing are those of the C# records in
 * `Host/Status/Models/` (`BedrockPriceCatalogueResponse`, `BedrockPriceOverrideResponse`, `BedrockPriceOverrideSavedResponse`,
 * `BedrockPriceRowResponse`, `BedrockAwsRateResponse`, `BedrockCatalogueModelResponse`, `UnpricedModelResponse` and
 * `SaveBedrockPriceOverrideRequest`), camelCase on the wire. All four endpoints need `settings.write`.
 *
 * It does not use `adminRequest`, because the writes answer problems that must be told apart: 400 names the request field that is wrong,
 * a stale 409 carries the live override in `current`, and a duplicate 409 does not.
 */
import { apiFetch } from "./apiFetch";

/** How the last AWS Price List refresh went (`PriceRefreshStatusResponse`). */
export interface BedrockPriceRefresh {
  lastRunStartedAt: string | null;
  lastRunFinishedAt: string | null;
  outcome: string | null;
  hadProblems: boolean;
}

/** One stored price row (`BedrockPriceRowResponse`); `source` is `AWS` or `CUR`, `derivedAt` an ISO timestamp. */
export interface BedrockPriceRow {
  route: string;
  modelKey: string;
  region: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion: number | null;
  cacheWrite5mPerMillion: number | null;
  cacheWrite1hPerMillion: number | null;
  source: string;
  derivedAt: string;
}

/** The AWS rate an override has no effect against (`BedrockAwsRateResponse`). */
export interface BedrockAwsRate {
  route: string;
  modelKey: string;
  region: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion: number | null;
  cacheWrite5mPerMillion: number | null;
  cacheWrite1hPerMillion: number | null;
}

/** An override's state as the server sends it. */
export type BedrockOverrideState = "InUse" | "NoEffect";

/** One operator price override (`BedrockPriceOverrideResponse`). `reviewBy` is a `yyyy-MM-dd` date. */
export interface BedrockPriceOverride {
  id: string;
  version: number;
  modelId: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion: number | null;
  cacheWritePerMillion: number | null;
  source: string | null;
  reviewBy: string | null;
  state: BedrockOverrideState;
  awsRate: BedrockAwsRate | null;
  reviewOverdue: boolean;
  invocationCount: number;
}

/** An invoked model nothing prices (`UnpricedModelResponse`). */
export interface BedrockUnpricedModel {
  modelId: string;
  invocationCount: number;
  lastSeenAt: string;
}

/** What would price a model: `None`, `AWS`, `CUR` or `Override` (`BedrockCatalogueModelResponse.PriceSource`). */
export type BedrockPriceSource = "None" | "AWS" | "CUR" | "Override";

/** One model Bedrock lists (`BedrockCatalogueModelResponse`). */
export interface BedrockCatalogueModel {
  modelId: string;
  displayName: string;
  familyName: string;
  route: string;
  priceSource: BedrockPriceSource;
  overrideId: string | null;
}

/** The whole page's data (`BedrockPriceCatalogueResponse`). */
export interface BedrockPriceCatalogue {
  refresh: BedrockPriceRefresh;
  prices: BedrockPriceRow[];
  overrides: BedrockPriceOverride[];
  unpriced: BedrockUnpricedModel[];
  models: BedrockCatalogueModel[];
  modelsAvailable: boolean;
}

/** The body of a create or an update (`SaveBedrockPriceOverrideRequest`); rates are invariant decimals. `version` is sent on an update. */
export interface SaveBedrockPriceOverride {
  modelId: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cacheReadPerMillion: number | null;
  cacheWritePerMillion: number | null;
  source: string | null;
  reviewBy: string | null;
  version?: number;
}

/** What a save answers (`BedrockPriceOverrideSavedResponse`): the stored override and a note on when it applies. */
export interface BedrockPriceOverrideSaved {
  override: BedrockPriceOverride;
  note: string;
}

/** The outcome of one call: success, or the failure told apart as the page needs. */
export type BedrockPriceResult<T> =
  | { kind: "ok"; response: T }
  | { kind: "denied" }
  | { kind: "stale"; current: BedrockPriceOverride | null }
  | { kind: "conflict"; message: string }
  | { kind: "invalid"; field: string | null; message: string }
  | { kind: "notFound" }
  | { kind: "error"; message: string };

interface Problem {
  detail?: string;
  title?: string;
  field?: string;
  current?: BedrockPriceOverride;
}

const catalogueLabel = "Bedrock prices";
const overridesPath = "/api/status/bedrock/price-overrides";

async function readProblem(response: Response): Promise<Problem> {
  try {
    return (await response.json()) as Problem;
  } catch {
    return {};
  }
}

function problemText(problem: Problem, status: number): string {
  return problem.detail ?? problem.title ?? `The ${catalogueLabel} endpoint returned ${String(status)}.`;
}

async function send<T>(path: string, init: RequestInit, expectBody: boolean): Promise<BedrockPriceResult<T>> {
  try {
    const headers: HeadersInit = { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) };
    const response = await apiFetch(path, { credentials: "same-origin", cache: "no-store", ...init, headers });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (response.status === 404) {
      return { kind: "notFound" };
    }

    if (response.status === 400) {
      const problem = await readProblem(response);
      return { kind: "invalid", field: problem.field ?? null, message: problemText(problem, response.status) };
    }

    if (response.status === 409) {
      const problem = await readProblem(response);
      return problem.current === undefined ? { kind: "conflict", message: problemText(problem, response.status) } : { kind: "stale", current: problem.current };
    }

    if (!response.ok) {
      return { kind: "error", message: problemText(await readProblem(response), response.status) };
    }

    return { kind: "ok", response: expectBody ? ((await response.json()) as T) : (undefined as T) };
  } catch {
    return { kind: "error", message: `The ${catalogueLabel} endpoint could not be reached.` };
  }
}

/** Calls `GET /api/status/bedrock/prices`. */
export function fetchBedrockPrices(): Promise<BedrockPriceResult<BedrockPriceCatalogue>> {
  return send<BedrockPriceCatalogue>("/api/status/bedrock/prices", { method: "GET" }, true);
}

/** Calls `POST /api/status/bedrock/price-overrides`. */
export function createBedrockPriceOverride(body: SaveBedrockPriceOverride): Promise<BedrockPriceResult<BedrockPriceOverrideSaved>> {
  return send<BedrockPriceOverrideSaved>(overridesPath, { method: "POST", body: JSON.stringify(body) }, true);
}

/** Calls `PUT /api/status/bedrock/price-overrides/{id}`; `body.version` is the version the caller read. */
export function updateBedrockPriceOverride(id: string, body: SaveBedrockPriceOverride): Promise<BedrockPriceResult<BedrockPriceOverrideSaved>> {
  return send<BedrockPriceOverrideSaved>(`${overridesPath}/${id}`, { method: "PUT", body: JSON.stringify(body) }, true);
}

/** Calls `DELETE /api/status/bedrock/price-overrides/{id}?version=`; the server answers 204. */
export function deleteBedrockPriceOverride(id: string, version: number): Promise<BedrockPriceResult<undefined>> {
  return send<undefined>(`${overridesPath}/${id}?version=${String(version)}`, { method: "DELETE" }, false);
}
